import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/** Sessões de diagnóstico (DG30 e DG60) de um serviço. */
export const fetchDgSessions = (serviceId) =>
  cached(`dg-sessions:${serviceId}`, () => run(supabase
    .from('dg_sessions')
    .select('*')
    .eq('service_id', serviceId)));

/** Checagens de uma sessão, na ordem em que foram lançadas. */
export const fetchDgChecks = (sessionId) =>
  cached(`dg-checks:${sessionId}`, () => run(supabase
    .from('dg_checks')
    .select('*')
    .eq('dg_session_id', sessionId)
    .order('sort_order')));

/**
 * Quantos diagnósticos com resultado o serviço já tem gravados.
 *
 * Bloqueia o "Substituir pela FIV" da TE: as transferências descartadas levam
 * junto o vínculo desses resultados. Sem cache: a pergunta é "agora".
 */
export const diagnosticosComResultado = async (serviceId) => {
  const sessoes = await run(supabase.from('dg_sessions').select('id').eq('service_id', serviceId)) || [];
  if (sessoes.length === 0) return 0;
  const checks = await run(supabase.from('dg_checks').select('id')
    .in('dg_session_id', sessoes.map(x => x.id))
    .not('result', 'is', null)) || [];
  return checks.length;
};

/** Grava a sessão (uma por serviço + tipo de checagem) e devolve o id. */
export const upsertDgSession = async (payload) => {
  const saved = await run(supabase
    .from('dg_sessions')
    .upsert(payload, { onConflict: 'service_id,check_type' })
    .select('id')
    .single());
  return saved.id;
};

/** Grava a sessão de diagnóstico e suas checagens como uma operação só. */
export const saveDgSession = queueable('dg.save',
  async (sessionPayload, laboratoryId, checkRows) => {
    const sessionId = await upsertDgSession(sessionPayload);
    await replaceDgChecks(sessionId, laboratoryId,
      checkRows.map(r => ({ ...r, dg_session_id: sessionId })));
    return sessionId;
  },
  { pendingOf: (sessionPayload, _lab, _rows, appData) => appData && {
      kind: `dg-${sessionPayload.check_type}`, id: sessionPayload.service_id,
      data: appData, label: `Sessão ${sessionPayload.check_type}`,
    } });

/**
 * Troca as checagens da sessão pelas informadas.
 *
 * O filtro por laboratory_id no delete não é redundante: sem ele a política de
 * RLS do DELETE não casa e o apagamento sai silenciosamente vazio.
 */
export const replaceDgChecks = async (sessionId, laboratoryId, rows) => {
  await run(supabase.from('dg_checks').delete()
    .eq('dg_session_id', sessionId)
    .eq('laboratory_id', laboratoryId));
  if (rows.length > 0) {
    await run(supabase.from('dg_checks').insert(rows));
  }
};
